import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  fetchUpcomingEventsForPerson,
  groupUpcomingEventsByQuizrun,
  isEventOngoing,
  type QuizrunGroup,
  type UpcomingEvent,
} from "@/api/eventDiscovery";
import { useToast } from "@/hooks/use-toast";
import { RegistrationErrorBoundary } from "@/components/profile/RegistrationErrorBoundary";
import { RegisteredCard } from "./upcoming-events/cards";
import { QuizrunSection } from "./upcoming-events/QuizrunSection";
import { logger, serializeError } from "@/lib/logger";

function SectionShell({
  children,
  heading = "Upcoming events",
  footer,
}: {
  children: React.ReactNode;
  /** Section heading; pass null to omit the header row entirely. */
  heading?: string | null;
  footer?: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      {heading && (
        <div className="mb-3">
          <h2 className="text-lg font-bold">{heading}</h2>
        </div>
      )}
      {children}
      {footer}
    </section>
  );
}

interface Props {
  personId: number;
  /** "registered" (profile) shows only the events the person is registered for.
   *  "single" (the /events/register page) shows just the one event named by
   *  `eventId`. Default shows every upcoming Quizrun with register controls. */
  variant?: "all" | "registered" | "single";
  /** The event to show in "single" variant. Ignored by other variants. */
  eventId?: number | null;
}

export default function UpcomingEventsSection({
  personId,
  variant = "all",
  eventId = null,
}: Props) {
  const registeredOnly = variant === "registered";
  const singleEvent = variant === "single";
  const [groups, setGroups] = useState<QuizrunGroup[] | null>(null);
  const [error, setError] = useState(false);
  const [activeEvent, setActiveEvent] = useState<UpcomingEvent | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const reload = useCallback(async () => {
    setError(false);
    try {
      const events = await fetchUpcomingEventsForPerson(personId);
      // fetchUpcomingEventsForPerson only returns events of not-yet-finished
      // quizruns, so past events are already excluded and there is no
      // cross-section duplication to de-dupe here. (An earlier quizrun-number
      // de-dupe wrongly hid a valid open event whenever the person had played
      // any prior event of the same Quizrun number — e.g. the virtual run.)
      const scoped = singleEvent
        ? events.filter((e) => e.event_id === eventId)
        : events;
      let groups = groupUpcomingEventsByQuizrun(scoped);
      // Profile view: keep only Quizruns the person is registered for, and drop
      // any whose registered event already has a submitted result — a played
      // run belongs in History (shown there, flagged in-progress while ongoing),
      // not as an actionable Current/Upcoming panel.
      if (registeredOnly) {
        groups = groups.filter(
          (g) => g.is_registered && !g.registered_event?.has_result,
        );
      }
      setGroups(groups);
    } catch (err) {
      logger.error("Failed to load upcoming events", serializeError(err));
      setError(true);
    }
  }, [personId, registeredOnly, singleEvent, eventId]);

  useEffect(() => {
    setGroups(null);
    void reload();
  }, [reload]);

  // Single-event page: auto-open the registration panel for the (unregistered)
  // target event so arriving from a "Register" link lands straight on the form.
  useEffect(() => {
    if (!singleEvent || !groups) return;
    const all = groups.flatMap((g) => [...g.cities, ...g.virtual]);
    const match = all.find((e) => e.event_id === eventId);
    if (match && !match.is_registered) setActiveEvent(match);
  }, [singleEvent, groups, eventId]);

  // Deep-link: ?event_id=<eventId> auto-opens the registration dialog for that
  // event once groups have loaded, then strips the param from the URL. Skipped
  // in "single" variant, where the param is the page's own routing state.
  useEffect(() => {
    if (singleEvent || !groups) return;
    const raw = searchParams.get("event_id");
    if (!raw) return;
    const targetId = Number(raw);
    if (!Number.isFinite(targetId)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("event_id");
          return next;
        },
        { replace: true },
      );
      return;
    }
    const all = groups.flatMap((g) => [...g.cities, ...g.virtual]);
    const match = all.find((e) => e.event_id === targetId);
    if (match) setActiveEvent(match);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("event_id");
        return next;
      },
      { replace: true },
    );
  }, [singleEvent, groups, searchParams, setSearchParams]);

  // Stripe checkout redirects back here with ?payment=success|cancelled. Toast
  // the outcome, reload so a completed payment's status surfaces, then strip the
  // param so a refresh doesn't re-toast.
  useEffect(() => {
    const status = searchParams.get("payment");
    if (status !== "success" && status !== "cancelled") return;
    if (status === "success") {
      toast({
        title: "Payment received — thank you! 🎉",
        description: "Your payment status will update shortly.",
      });
      void reload();
    } else {
      toast({ title: "Payment cancelled", description: "No charge was made." });
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("payment");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, toast, reload]);

  // One "All events" link, always as a footer line below the content (never a
  // top-right header action) — a single consistent treatment across states.
  const allEventsFooter = (
    <div className="mt-4">
      <Link
        to="/events"
        className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
      >
        Browse all events
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );

  // Single-event register page has no heading. Registered (profile) view lists
  // only events you've registered for, so scope the label with "Your".
  const heading = singleEvent
    ? null
    : registeredOnly
      ? "Your upcoming events"
      : "Upcoming events";

  if (error) {
    return (
      <SectionShell heading={heading} footer={allEventsFooter}>
        <p className="text-sm text-muted-foreground">
          Couldn't load events. Please refresh the page.
        </p>
      </SectionShell>
    );
  }

  if (groups === null) {
    return (
      <SectionShell
        heading={heading}
        footer={singleEvent ? allEventsFooter : undefined}
      >
        <div className="flex items-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Loading…
        </div>
      </SectionShell>
    );
  }

  if (groups.length === 0) {
    return (
      <SectionShell heading={heading} footer={allEventsFooter}>
        {registeredOnly ? (
          <p className="text-sm text-muted-foreground">
            You haven't registered for any upcoming events yet.
          </p>
        ) : singleEvent ? (
          <p className="text-sm text-muted-foreground">
            This event isn't open for registration.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            No upcoming events scheduled yet — check back soon!
          </p>
        )}
      </SectionShell>
    );
  }

  // Registered Quizruns auto-expand. Unregistered ones expand only when there
  // are <= 2 total groups; otherwise collapse to save scroll depth on mobile.
  const collapseUnregistered = groups.length > 2;

  // Toggle the inline panel: clicking the active event's button closes it.
  const handleToggle = (e: UpcomingEvent) =>
    setActiveEvent((cur) => (cur?.event_id === e.event_id ? null : e));

  const renderGroups = (list: QuizrunGroup[]) =>
    list.map((g) => (
      <QuizrunSection
        key={g.quizrun_number}
        group={g}
        defaultOpen={g.is_registered || !collapseUnregistered}
        personId={personId}
        activeEventId={activeEvent?.event_id ?? null}
        onToggleEvent={handleToggle}
        onChanged={reload}
        onCloseEvent={() => setActiveEvent(null)}
        flat={singleEvent}
      />
    ));

  // Profile view: an ongoing event ("current") is shown in its own section
  // above the still-upcoming ones. A group counts as current when the event the
  // person is registered for is ongoing right now.
  if (registeredOnly) {
    const isGroupCurrent = (g: QuizrunGroup) =>
      g.registered_event != null && isEventOngoing(g.registered_event);
    // Partition in a single pass so a clock tick between two filters can't
    // double-place or drop a group sitting on the ongoing/upcoming boundary.
    const currentGroups: QuizrunGroup[] = [];
    const upcomingGroups: QuizrunGroup[] = [];
    for (const g of groups)
      (isGroupCurrent(g) ? currentGroups : upcomingGroups).push(g);

    // Render cards directly — no outer QuizrunSection collapsible — so each
    // registered event is a single frame (one border) rather than header+card.
    const renderRegisteredCards = (list: QuizrunGroup[]) =>
      list.map((g) => {
        if (!g.registered_event) return null;
        const label = g.quizrun_title;
        return (
          <RegistrationErrorBoundary key={g.quizrun_number}>
            <RegisteredCard
              event={g.registered_event}
              personId={personId}
              active={activeEvent?.event_id === g.registered_event.event_id}
              onManage={handleToggle}
              onChanged={reload}
              onClose={() => setActiveEvent(null)}
              quizrunLabel={label}
            />
          </RegistrationErrorBoundary>
        );
      });

    return (
      <>
        {currentGroups.length > 0 && (
          <SectionShell heading="Current events">
            <div className="space-y-3">
              {renderRegisteredCards(currentGroups)}
            </div>
          </SectionShell>
        )}
        {upcomingGroups.length > 0 && (
          <SectionShell heading="Your upcoming events" footer={allEventsFooter}>
            <div className="space-y-3">
              {renderRegisteredCards(upcomingGroups)}
            </div>
          </SectionShell>
        )}
        {/* All groups are current: still surface the "browse all" affordance. */}
        {upcomingGroups.length === 0 && (
          <SectionShell heading={null} footer={allEventsFooter}>
            {null}
          </SectionShell>
        )}
      </>
    );
  }

  return (
    <SectionShell heading={heading} footer={allEventsFooter}>
      {renderGroups(groups)}
    </SectionShell>
  );
}
